import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartAddEvent } from '@theme/events';

/**
 * @extends {Component}
 */
class CartGiftWrapping extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.checkbox = this.querySelector('.cart-gift-wrapping__checkbox');
    this.panel = this.querySelector('.cart-gift-wrapping__panel');
    this.checkbox?.addEventListener('change', this.#handleToggle);
    this.querySelectorAll('input[name^="gift-wrap-color-"]').forEach((input) => {
      input.addEventListener('change', this.#handleOptionChange);
    });
    this.querySelector('.cart-gift-wrapping__quantity')?.addEventListener('change', this.#handleOptionChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.checkbox?.removeEventListener('change', this.#handleToggle);
    this.querySelectorAll('input[name^="gift-wrap-color-"]').forEach((input) => {
      input.removeEventListener('change', this.#handleOptionChange);
    });
    this.querySelector('.cart-gift-wrapping__quantity')?.removeEventListener('change', this.#handleOptionChange);
  }

  #getSectionIds() {
    const sectionsToUpdate = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  #getSelectedVariantId() {
    const selectedColor = this.querySelector('input[name^="gift-wrap-color-"]:checked');
    return selectedColor instanceof HTMLInputElement ? Number(selectedColor.value) : null;
  }

  #getSelectedQuantity() {
    const quantityInput = this.querySelector('.cart-gift-wrapping__quantity');
    if (quantityInput instanceof HTMLSelectElement || quantityInput instanceof HTMLInputElement) {
      return Number(quantityInput.value) || 1;
    }
    return 1;
  }

  #setPanelOpen(isOpen) {
    this.panel?.classList.toggle('is-open', isOpen);
  }

  #showError(message) {
    const errorMessage = this.querySelector('[ref="errorMessage"]');
    if (!(errorMessage instanceof HTMLElement)) return;
    errorMessage.textContent = message;
    errorMessage.classList.remove('hidden');
  }

  #clearError() {
    const errorMessage = this.querySelector('[ref="errorMessage"]');
    if (!(errorMessage instanceof HTMLElement)) return;
    errorMessage.textContent = '';
    errorMessage.classList.add('hidden');
  }

  async #refreshSections(data) {
    this.#getSectionIds().forEach((sectionId) => {
      if (data.sections?.[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    });
  }

  async #addGiftWrap() {
    const variantId = this.#getSelectedVariantId();
    const quantity = this.#getSelectedQuantity();
    if (!variantId) return;

    this.#clearError();
    const body = JSON.stringify({
      items: [{ id: variantId, quantity }],
      sections: Array.from(this.#getSectionIds()).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (data.status) {
      this.#showError(data.message || data.description || 'Unable to add gift wrapping.');
      if (this.checkbox instanceof HTMLInputElement) {
        this.checkbox.checked = false;
      }
      this.#setPanelOpen(false);
      return;
    }

    await this.#refreshSections(data);
    this.#setPanelOpen(true);
    this.dispatchEvent(new CartAddEvent(data, '', { source: 'cart-gift-wrapping', itemCount: quantity }));
  }

  async #removeGiftWrap() {
    const line = Number(this.dataset.giftWrapLine);
    if (!line) {
      this.#setPanelOpen(false);
      return;
    }

    this.#clearError();
    const body = JSON.stringify({
      line,
      quantity: 0,
      sections: Array.from(this.#getSectionIds()).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (data.errors) {
      this.#showError(typeof data.errors === 'string' ? data.errors : 'Unable to remove gift wrapping.');
      if (this.checkbox instanceof HTMLInputElement) {
        this.checkbox.checked = true;
      }
      return;
    }

    await this.#refreshSections(data);
    this.#setPanelOpen(false);
  }

  #handleToggle = async () => {
    if (!(this.checkbox instanceof HTMLInputElement)) return;

    this.checkbox.disabled = true;

    try {
      if (this.checkbox.checked) {
        this.#setPanelOpen(true);
        await this.#addGiftWrap();
      } else {
        await this.#removeGiftWrap();
      }
    } finally {
      this.checkbox.disabled = false;
    }
  };

  #handleOptionChange = async () => {
    if (!(this.checkbox instanceof HTMLInputElement) || !this.checkbox.checked) return;

    this.checkbox.disabled = true;
    try {
      await this.#removeGiftWrap();
      if (this.checkbox instanceof HTMLInputElement) {
        this.checkbox.checked = true;
      }
      await this.#addGiftWrap();
    } finally {
      this.checkbox.disabled = false;
    }
  };
}

if (!customElements.get('cart-gift-wrapping')) {
  customElements.define('cart-gift-wrapping', CartGiftWrapping);
}
